/**
 * The pinned model identifiers — requirement 4.8, first half.
 *
 * One module names every model the system is allowed to call, so a call site cannot introduce a
 * string of its own. `verify:models` (see `verify-models.ts`) is the second half: it asks the
 * provider whether each of these exists.
 *
 * ## The open question these carry
 *
 * `gpt-5.4` and `gpt-5.4-mini` are not names this project chose. They are the identifiers the
 * dashboard being replaced passes to the OpenAI SDK in four places —
 * `server/routes/supportWidgetRoutes.ts:119`, `server/routes/aiRoutes.ts:627`,
 * `server/routes/externalAIRoutes.ts:885`, and `server/services/juanchoWhatsAppService.ts:290`
 * — and requirement 4.8 exists because nobody has verified that either one is real. If they are
 * not, the customer-facing support triage in production is failing on every request.
 *
 * They are pinned here rather than dropped even though only one caller survives the rebuild: the
 * support ticket form, which spec 3 rebuilds as a native form and which is the only path that
 * calls a model at all. Whether it uses the standard or the compact tier is spec 3's decision;
 * pinning both now means that decision cannot introduce an identifier this check has not seen.
 * The three other legacy call sites are cut with the Agent AI and the Juancho widget.
 *
 * Keyed by tier rather than by purpose on purpose. A key like `supportTriage` would claim a
 * mapping between a model and a job that no code in this repository makes yet.
 */
import { z } from 'zod';
export const modelPins = {
    /**
     * The standard tier. Legacy usage: support ticket triage and the dashboard's AI routes —
     * summarising a ticket, drafting a reply, reading an attached screenshot.
     */
    standard: 'gpt-5.4',
    /**
     * The cheaper tier. Legacy usage: the WhatsApp auto-reply, which is cut. Carried as a pin
     * because it is half of the open question above, not because something calls it today.
     */
    compact: 'gpt-5.4-mini',
};
/** Closed configuration vocabulary for selecting one of the verified pins. */
export const modelTiers = Object.freeze(Object.keys(modelPins));
export const modelTierSchema = z.enum(modelTiers);
/** Every pinned identifier, in tier order. What `verify:models` checks. */
export const pinnedModelIds = Object.freeze(Object.values(modelPins));
/**
 * For any payload that carries a model identifier. Nothing in this spec sends one; it exists so
 * that when spec 3's triage endpoint reports which model handled a ticket, the field cannot hold
 * an identifier outside the pinned set.
 */
export const pinnedModelIdSchema = z.enum(modelPins);
//# sourceMappingURL=models.js.map